import { useMemo, useRef } from 'react'
import { MosquetaoTabletop } from './ObjetosCordasTabletop'
import { AnelDeFita } from '../cordas/AnelDeFita'
import { useFrame } from '@react-three/fiber'
import { useEquipamentosNaCena } from './contextoEquipamentos'
import * as THREE from 'three'
import {
  ALCA_DO_NO_M,
  definicaoDaFuncao,
  localizarPontoNoMundo,
  tracadoDaCorda,
  trechosDaFita,
  comprimentoDoFioSemCarga,
  RAIO_BANZO_ESCADA_M,
  barraDoMosquetao,
  lugarNaBarra,
  naBarra,
  type NoDaPonta as NoDaPontaDominio,
  percursoNoMundo,
  type AncoragemTabletop,
  type CenaTabletop,
  type CordaTabletop,
  type FuncaoCorda,
  type ObjetoTabletop,
  type ReferenciaPonto,
  type SentidoCorda,
  type VetorTabletop,
} from '../../dominio/tabletop'
import { folgasDeDesenho } from '../../dominio/tabletop/catenariaCorda'

/**
 * As cordas na cena 3D: um tubo contínuo com a capa trançada, pelo traçado de
 * `tracadoCorda.ts` (contorna a roldana das polias, arredonda as dobras, pende
 * nos trechos com folga) e com nó oito nas pontas presas.
 *
 * As posições são recalculadas a cada mudança dos objetos a partir de
 * `objeto + ponto` — a corda não guarda coordenadas, então acompanha o objeto
 * que se move ou muda de forma. A cor vem da função (ver `funcoesCorda.ts`).
 */

export const RAIO_CORDA_M = 0.014
export const RAIO_SELECIONADA_M = 0.032
export const RAIO_ALVO_TOQUE_M = 0.09
const EIXO_Y = new THREE.Vector3(0, 1, 0)

interface PropsSegmento {
  de: VetorTabletop
  para: VetorTabletop
  cor: string
  raio: number
  opacidade?: number
}

export function orientacao(de: VetorTabletop, para: VetorTabletop) {
  const direcao = new THREE.Vector3(para[0] - de[0], para[1] - de[1], para[2] - de[2])
  const comprimento = direcao.length()
  const quaternion = new THREE.Quaternion()
  if (comprimento > 1e-9) quaternion.setFromUnitVectors(EIXO_Y, direcao.clone().divideScalar(comprimento))
  return { comprimento, quaternion, direcao }
}

export function SegmentoCorda({ de, para, cor, raio, opacidade = 1 }: PropsSegmento) {
  const { comprimento, quaternion } = useMemo(() => orientacao(de, para), [de, para])
  if (comprimento < 1e-6) return null
  return (
    <mesh
      raycast={() => null}
      position={[(de[0] + para[0]) / 2, (de[1] + para[1]) / 2, (de[2] + para[2]) / 2]}
      quaternion={quaternion}
    >
      <cylinderGeometry args={[raio, raio, comprimento, 8]} />
      <meshStandardMaterial color={cor} roughness={0.7} transparent={opacidade < 1} opacity={opacidade} />
    </mesh>
  )
}

export interface SetaDeTracao {
  readonly posicao: VetorTabletop
  /** Vetor unitário no sentido da força. */
  readonly direcao: VetorTabletop
  /**
   * `puxada`: ao longo da corda, no sentido em que ela é puxada. `ponta`: perto
   * de uma ponta, no sentido em que a corda puxa o que está preso ali.
   */
  readonly tipo: 'puxada' | 'ponta'
}

const ESPACO_ENTRE_SETAS_M = 2.5
const MAXIMO_DE_SETAS_POR_TRECHO = 8
/** Distância da ponta até a seta de ponta. */
const RECUO_SETA_DE_PONTA_M = 0.55

/** Quantas setas cabem num trecho de `comprimento` metros e onde ficam, de 0 a 1. */
export function fracoesDasSetas(comprimento: number): readonly number[] {
  if (comprimento < 0.3) return []
  const n = Math.min(MAXIMO_DE_SETAS_POR_TRECHO, Math.max(1, Math.round(comprimento / ESPACO_ENTRE_SETAS_M)))
  return Array.from({ length: n }, (_, i) => (i + 0.5) / n)
}

const unitario = (de: VetorTabletop, para: VetorTabletop): { u: VetorTabletop; comprimento: number } => {
  const d = [para[0] - de[0], para[1] - de[1], para[2] - de[2]] as const
  const comprimento = Math.hypot(d[0], d[1], d[2])
  return { u: comprimento > 1e-9 ? [d[0] / comprimento, d[1] / comprimento, d[2] / comprimento] : [0, 0, 0], comprimento }
}

const ao = (de: VetorTabletop, u: VetorTabletop, distancia: number): VetorTabletop => [
  de[0] + u[0] * distancia,
  de[1] + u[1] * distancia,
  de[2] + u[2] * distancia,
]

/**
 * As setas de tração de uma corda.
 *
 *  - Com `sentido`, a corda é puxada num sentido: há setas ao longo de todos os
 *    trechos, apontando para onde ela anda (`puxada`).
 *  - Sem `sentido`, a corda só se tensiona: há uma seta perto de cada ponta,
 *    apontando para dentro da corda, que é para onde ela puxa o que está preso
 *    ali (`ponta`). Numa estabilização, por exemplo, a seta na escada aponta
 *    para a viatura.
 *
 * Trechos com ponta solta (`null`) e trechos frouxos (com folga) não têm seta:
 * corda frouxa não puxa.
 */
export function setasDaCorda(
  pontos: ReadonlyArray<VetorTabletop | null>,
  sentido: SentidoCorda | undefined,
  folgas: readonly number[] = [],
): readonly SetaDeTracao[] {
  const trechos: Array<{ de: VetorTabletop; para: VetorTabletop } | null> = []
  for (let i = 1; i < pontos.length; i += 1) {
    const de = pontos[i - 1]
    const para = pontos[i]
    trechos.push(de && para && !((folgas[i - 1] ?? 0) > 0) ? { de, para } : null)
  }
  const setas: SetaDeTracao[] = []
  if (sentido) {
    for (const trecho of trechos) {
      if (!trecho) continue
      const { de, para } = trecho
      const [origem, destino] = sentido === 'paraFim' ? [de, para] : [para, de]
      const { u, comprimento } = unitario(origem, destino)
      for (const f of fracoesDasSetas(comprimento)) setas.push({ posicao: ao(origem, u, comprimento * f), direcao: u, tipo: 'puxada' })
    }
    return setas
  }
  const primeiro = trechos[0]
  const ultimo = trechos.length > 0 ? trechos[trechos.length - 1] : null
  if (primeiro) {
    const { u, comprimento } = unitario(primeiro.de, primeiro.para)
    if (comprimento >= 0.3) setas.push({ posicao: ao(primeiro.de, u, Math.min(RECUO_SETA_DE_PONTA_M, comprimento / 2)), direcao: u, tipo: 'ponta' })
  }
  if (ultimo) {
    const { u, comprimento } = unitario(ultimo.para, ultimo.de)
    if (comprimento >= 0.3) setas.push({ posicao: ao(ultimo.para, u, Math.min(RECUO_SETA_DE_PONTA_M, comprimento / 2)), direcao: u, tipo: 'ponta' })
  }
  return setas
}

/** Cone de seta: a ponta aponta na direção `direcao`. */
export function SetaCone({ posicao, direcao, cor, tamanho = 1 }: { posicao: VetorTabletop; direcao: VetorTabletop; cor: string; tamanho?: number }) {
  const quaternion = useMemo(() => {
    const q = new THREE.Quaternion()
    const d = new THREE.Vector3(direcao[0], direcao[1], direcao[2])
    if (d.lengthSq() > 1e-12) q.setFromUnitVectors(EIXO_Y, d.normalize())
    return q
  }, [direcao])
  return (
    <mesh raycast={() => null} position={[posicao[0], posicao[1], posicao[2]]} quaternion={quaternion} renderOrder={5}>
      <coneGeometry args={[0.085 * tamanho, 0.3 * tamanho, 14]} />
      <meshStandardMaterial color={cor} emissive={cor} emissiveIntensity={0.55} roughness={0.4} />
    </mesh>
  )
}

/** Comprimento de uma volta do trançado da capa, em metros: a textura se repete a cada tanto de corda. */
const PERIODO_TRANCADO_M = 0.08
/** Velocidade da corda na puxada animada, em m/s. */
export const VELOCIDADE_PUXADA_M_S = 0.6

const texturasDaCapa = new Map<string, THREE.Texture | null>()

/**
 * A capa trançada da corda (kernmantle), desenhada num canvas: a cor da função
 * com fios em diagonal mais escuros e pontinhos, como nas cordas de verdade.
 * Uma textura por cor, compartilhada; sem `document` (testes), sem textura.
 */
export function texturaDaCapa(cor: string): THREE.Texture | null {
  if (texturasDaCapa.has(cor)) return texturasDaCapa.get(cor)!
  let textura: THREE.Texture | null = null
  const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null
  const ctx = canvas?.getContext?.('2d') ?? null
  if (canvas && ctx) {
    canvas.width = 64
    canvas.height = 32
    ctx.fillStyle = cor
    ctx.fillRect(0, 0, 64, 32)
    // os fios da capa: faixas em diagonal, alternando mais claro e mais escuro
    for (let k = -32; k < 64; k += 8) {
      ctx.strokeStyle = 'rgba(0,0,0,0.22)'
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.moveTo(k, 0)
      ctx.lineTo(k + 32, 32)
      ctx.stroke()
      ctx.strokeStyle = 'rgba(255,255,255,0.14)'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(k + 4, 32)
      ctx.lineTo(k + 36, 0)
      ctx.stroke()
    }
    // os pontinhos escuros do desenho da capa
    ctx.fillStyle = 'rgba(0,0,0,0.45)'
    for (let x = 4; x < 64; x += 16) for (let y = 6; y < 32; y += 16) ctx.fillRect(x + ((y / 16) % 2) * 8, y, 3, 3)
    textura = new THREE.CanvasTexture(canvas)
    textura.wrapS = THREE.RepeatWrapping
    textura.wrapT = THREE.RepeatWrapping
    textura.colorSpace = THREE.SRGBColorSpace
  }
  texturasDaCapa.set(cor, textura)
  return textura
}

/**
 * Um tubo pela linha (já com as curvas), com a textura repetindo ao longo do
 * comprimento: o `u` da geometria vai de 0 a 1 na corda inteira e é esticado
 * para dar uma volta do trançado a cada `PERIODO_TRANCADO_M`.
 */
export function geometriaDoTubo(linha: readonly VetorTabletop[], raio: number): THREE.TubeGeometry | null {
  if (linha.length < 2) return null
  const caminho = new THREE.CurvePath<THREE.Vector3>()
  let comprimento = 0
  for (let i = 1; i < linha.length; i += 1) {
    const a = new THREE.Vector3(...linha[i - 1]!)
    const b = new THREE.Vector3(...linha[i]!)
    const d = a.distanceTo(b)
    if (d < 1e-6) continue
    caminho.add(new THREE.LineCurve3(a, b))
    comprimento += d
  }
  if (comprimento < 1e-4) return null
  const segmentos = Math.min(2000, Math.max(linha.length * 2, Math.ceil(comprimento / 0.03)))
  const geometria = new THREE.TubeGeometry(caminho, segmentos, raio, 8, false)
  const uv = geometria.getAttribute('uv') as THREE.BufferAttribute
  for (let k = 0; k < uv.count; k += 1) uv.setX(k, uv.getX(k) * (comprimento / PERIODO_TRANCADO_M))
  uv.needsUpdate = true
  return geometria
}

/** O tubo com a capa andando: uma cópia da textura, com o deslocamento mudando a cada quadro. */
function TuboCorrendo({ linha, textura, sentido }: { linha: readonly VetorTabletop[]; textura: THREE.Texture; sentido: number }) {
  const propria = useMemo(() => {
    const t = textura.clone()
    t.needsUpdate = true
    return t
  }, [textura])
  useFrame((_, dt) => {
    // a capa anda para o fim do percurso: o padrão vai para u maior, o deslocamento diminui
    propria.offset.x -= (sentido * VELOCIDADE_PUXADA_M_S * dt) / PERIODO_TRANCADO_M
  })
  return <TuboDaCorda nome="corda-tubo" linha={linha} cor="#ffffff" raio={RAIO_CORDA_M} textura={propria} />
}

function TuboDaCorda({ linha, cor, raio, opacidade = 1, nome, textura }: { linha: readonly VetorTabletop[]; cor: string; raio: number; opacidade?: number; nome: string; textura?: THREE.Texture | null }) {
  const geometria = useMemo(() => geometriaDoTubo(linha, raio), [linha, raio])
  if (!geometria) return null
  return (
    <mesh name={nome} geometry={geometria} raycast={() => null}>
      <meshStandardMaterial color={textura ? '#ffffff' : cor} map={textura ?? null} roughness={0.75} transparent={opacidade < 1} opacity={opacidade} />
    </mesh>
  )
}

/** O nó oito com a alça, de `inicio` (onde a alça clipa) até o nó, ao longo do +Y local. */
function NoOitoComAlca({ inicio, cor, raio }: { inicio: number; cor: string; raio: number }) {
  return (
    <>
      {/* a alça: os dois fios da corda, do mosquetão até o nó */}
      <mesh position={[0, inicio + ALCA_DO_NO_M / 2 - 0.02, 0]} scale={[0.7, (ALCA_DO_NO_M - 0.02) / 0.07, 1]} raycast={() => null}>
        <torusGeometry args={[0.035, raio * 0.8, 8, 20]} />
        <meshStandardMaterial color={cor} roughness={0.75} />
      </mesh>
      {/* o nó oito: dois laços cruzados */}
      {[0, 1].map((k) => (
        <mesh key={k} position={[0, inicio + ALCA_DO_NO_M - 0.01 + k * 0.03, 0]} rotation={[Math.PI / 2, 0, k * 0.6]} raycast={() => null}>
          <torusGeometry args={[raio * 1.6, raio * 0.85, 8, 16]} />
          <meshStandardMaterial color={cor} roughness={0.8} />
        </mesh>
      ))}
    </>
  )
}

/**
 * Como a ponta da corda se prende, do ponto de ancoragem para dentro da corda
 * (o +Y local aponta para a corda): anel de fita, mosquetão e nó oito numa
 * estrutura; mosquetão e nó numa peça com argola ou numa carga; só o nó na
 * fita por envolvimento. As medidas batem com `COMPRIMENTO_CONEXAO_M`.
 */
export function ConexaoDaPonta({ no, cor, raio, fator = 1 }: { no: NoDaPontaDominio; cor: string; raio: number; fator?: number }) {
  const quaternion = useMemo(() => {
    const q = new THREE.Quaternion()
    const y = new THREE.Vector3(...no.direcao)
    if (no.normalDaAlca) {
      // a alça do nó (no plano XY local) fica perpendicular à barra do mosquetão: a barra passa por dentro dela
      const z = new THREE.Vector3(...no.normalDaAlca)
      z.addScaledVector(y, -z.dot(y))
      if (z.lengthSq() > 1e-9) {
        z.normalize()
        const x = new THREE.Vector3().crossVectors(y, z)
        return q.setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, z))
      }
    }
    q.setFromUnitVectors(EIXO_Y, y)
    return q
  }, [no.direcao, no.normalDaAlca])
  const corFita = definicaoDaFuncao('fita').cor
  const carregada = !no.frouxa
  if (no.conexao === 'v-de-aneis' && no.apoios) {
    // na escada: um anel em cada banzo, os dois no mosquetão, e o nó da corda no mosquetão
    const d = 0.18 * fator
    const mosquetao: VetorTabletop = [no.ponta[0] + no.direcao[0] * d, no.ponta[1] + no.direcao[1] * d, no.ponta[2] + no.direcao[2] * d]
    // os anéis abraçam a barra de baixo do mosquetão (o +Y dele aponta para a corda), lado a lado
    const deBaixo = barraDoMosquetao(mosquetao, no.direcao, -1, fator)
    const barras = no.apoios.map((_, i) => naBarra(deBaixo, lugarNaBarra(i, no.apoios!.length, fator)))
    return (
      <group name="conexao-v-de-aneis">
        {no.apoios.map((apoio, i) => (
          <AnelDeFita
            key={i}
            apoio={apoio}
            para={barras[i]!.centro}
            barra={barras[i]!}
            eixo={no.eixo ?? null}
            raioDoApoio={RAIO_BANZO_ESCADA_M * fator}
            carregada={carregada}
            comprimentoSemCargaM={comprimentoDoFioSemCarga(Math.hypot(mosquetao[0] - apoio[0], mosquetao[1] - apoio[1], mosquetao[2] - apoio[2]), RAIO_BANZO_ESCADA_M * fator)}
            cor={corFita}
            escala={fator}
          />
        ))}
        <group position={[mosquetao[0], mosquetao[1], mosquetao[2]]} quaternion={quaternion} scale={fator}>
          <MosquetaoTabletop semAlvo />
          <NoOitoComAlca inicio={0.04} cor={cor} raio={raio} />
        </group>
      </group>
    )
  }
  if (no.conexao === 'anel-e-mosquetao') {
    // o anel de fita em volta do ponto da estrutura, o mosquetão nele e o nó da corda no mosquetão
    const d = 0.18 * fator
    const mosquetao: VetorTabletop = [no.ponta[0] + no.direcao[0] * d, no.ponta[1] + no.direcao[1] * d, no.ponta[2] + no.direcao[2] * d]
    const barra = barraDoMosquetao(mosquetao, no.direcao, -1, fator)
    return (
      <group name="conexao-anel-e-mosquetao">
        <AnelDeFita
          apoio={no.ponta}
          para={barra.centro}
          barra={barra}
          raioDoApoio={0.02 * fator}
          carregada={carregada}
          cor={corFita}
          escala={fator}
        />
        <group position={[mosquetao[0], mosquetao[1], mosquetao[2]]} quaternion={quaternion} scale={fator}>
          <MosquetaoTabletop semAlvo />
          <NoOitoComAlca inicio={0.04} cor={cor} raio={raio} />
        </group>
      </group>
    )
  }
  return (
    <group name={`conexao-${no.conexao}`} position={[no.ponta[0], no.ponta[1], no.ponta[2]]} quaternion={quaternion} scale={fator}>
      {no.conexao === 'mosquetao' && (
        <>
          <group position={[0, 0.05, 0]}>
            <MosquetaoTabletop semAlvo />
          </group>
          <NoOitoComAlca inicio={0.09} cor={cor} raio={raio} />
        </>
      )}
      {no.conexao === 'no' && <NoOitoComAlca inicio={0} cor={cor} raio={raio} />}
    </group>
  )
}

function CordaNaCena({
  corda,
  cena,
  pontos,
  selecionada,
  interativa,
  mostrarSetas,
  aoSelecionar,
}: {
  corda: CordaTabletop
  cena: CenaTabletop
  pontos: ReadonlyArray<VetorTabletop | null>
  selecionada: boolean
  interativa: boolean
  mostrarSetas: boolean
  aoSelecionar: (id: string) => void
}) {
  const cor = definicaoDaFuncao(corda.funcao).cor
  const textura = texturaDaCapa(cor)
  const inicioToque = useRef<{ x: number; y: number } | null>(null)
  const setas = mostrarSetas ? setasDaCorda(pontos, corda.sentido, corda.folgas) : []
  const { fatorDaPeca, fatorDasConexoes, animarPuxada } = useEquipamentosNaCena()
  const tracado = useMemo(() => {
    // a catenária é só do desenho: setas e contas seguem com as folgas definidas
    const folgas = folgasDeDesenho(cena, corda)
    const desenhada = folgas === corda.folgas ? corda : { ...corda, folgas }
    return tracadoDaCorda(cena, desenhada, { fatorDaPeca: (o) => fatorDaPeca(o.id), fatorDasConexoes })
  }, [cena, corda, fatorDaPeca, fatorDasConexoes])
  if (corda.funcao === 'fita') {
    return <FitaNaCena corda={corda} cena={cena} cor={cor} selecionada={selecionada} interativa={interativa} mostrarSetas={mostrarSetas} aoSelecionar={aoSelecionar} />
  }
  // na puxada animada, a corda com sentido corre: a capa anda ao longo do tubo
  const correndo = animarPuxada && corda.sentido ? (corda.sentido === 'paraFim' ? 1 : -1) : 0

  return (
    <group name={`corda-${corda.id}`}>
      {tracado.linhas.map((linha, i) => (
        <group key={i}>
          {selecionada && <TuboDaCorda nome="corda-contorno" linha={linha} cor="#ffffff" raio={RAIO_SELECIONADA_M} opacidade={0.55} />}
          {correndo && textura ? (
            <TuboCorrendo linha={linha} textura={textura} sentido={correndo} />
          ) : (
            <TuboDaCorda nome="corda-tubo" linha={linha} cor={cor} raio={RAIO_CORDA_M} textura={textura} />
          )}
          {interativa && (
            // alvo de toque mais grosso que a corda: um fio de 3 cm não pode exigir pontaria de milímetro
            <AlvoDaCorda
              linha={linha}
              aoTocar={(evento, tipo) => {
                if (tipo === 'inicio') {
                  inicioToque.current = { x: evento.clientX, y: evento.clientY }
                  return
                }
                const inicio = inicioToque.current
                inicioToque.current = null
                // arrasto de órbita termina sobre a corda e não é seleção
                if (inicio && Math.hypot(evento.clientX - inicio.x, evento.clientY - inicio.y) > 6) return
                aoSelecionar(corda.id)
              }}
            />
          )}
        </group>
      ))}
      {tracado.nos.map((no, i) => (
        <ConexaoDaPonta key={`no-${i}`} no={no} cor={cor} raio={RAIO_CORDA_M} fator={fatorDasConexoes} />
      ))}
      {setas.map((seta, i) => (
        <SetaCone key={`seta-${i}`} posicao={seta.posicao} direcao={seta.direcao} cor={cor} />
      ))}
    </group>
  )
}

/**
 * A corda de função `fita`: um anel de fita chata por trecho, abraçando a
 * estrutura (na escada, o banzo) e terminando na peça. Com peso na peça a fita
 * estica; sem peso, ou com folga no trecho, ela pende. A seta, perto da peça,
 * só aparece com a fita sob carga: fita frouxa não puxa.
 */
function FitaNaCena({
  corda,
  cena,
  cor,
  selecionada,
  interativa,
  mostrarSetas,
  aoSelecionar,
}: {
  corda: CordaTabletop
  cena: CenaTabletop
  cor: string
  selecionada: boolean
  interativa: boolean
  mostrarSetas: boolean
  aoSelecionar: (id: string) => void
}) {
  const inicioToque = useRef<{ x: number; y: number } | null>(null)
  const { fatorDaPeca } = useEquipamentosNaCena()
  const trechos = useMemo(() => trechosDaFita(cena, corda, fatorDaPeca), [cena, corda, fatorDaPeca])
  return (
    <group name={`corda-${corda.id}`}>
      {trechos.map((t, i) => {
        if (!t) return null
        const { apoio, ponta } = t
        const { u, comprimento } = unitario(ponta, apoio.ponto)
        return (
          <group key={i}>
            {selecionada && <SegmentoCorda de={apoio.ponto} para={ponta} cor="#ffffff" raio={RAIO_SELECIONADA_M} opacidade={0.5} />}
            <AnelDeFita
              apoio={apoio.ponto}
              para={ponta}
              eixo={apoio.eixo}
              raioDoApoio={apoio.raio}
              carregada={t.carregada}
              comprimentoSemCargaM={t.comprimentoSemCargaM}
              cor={cor}
              barra={t.barra ?? null}
            />
            {/* a fita clipa num mosquetão, e o mosquetão no olhal da peça */}
            {t.mosquetao && (
              <group name="mosquetao-da-fita" position={[t.mosquetao.centro[0], t.mosquetao.centro[1], t.mosquetao.centro[2]]}>
                <MosquetaoTabletop semAlvo eixo={t.mosquetao.eixo} />
              </group>
            )}
            {mostrarSetas && t.carregada && comprimento > 0.6 && <SetaCone posicao={ao(ponta, u, 0.3)} direcao={u} cor={cor} tamanho={0.7} />}
            {interativa && (
              <AlvoDaCorda
                linha={[apoio.ponto, ponta]}
                aoTocar={(evento, tipo) => {
                  if (tipo === 'inicio') {
                    inicioToque.current = { x: evento.clientX, y: evento.clientY }
                    return
                  }
                  const inicio = inicioToque.current
                  inicioToque.current = null
                  if (inicio && Math.hypot(evento.clientX - inicio.x, evento.clientY - inicio.y) > 6) return
                  aoSelecionar(corda.id)
                }}
              />
            )}
          </group>
        )
      })}
    </group>
  )
}

/** O tubo invisível e grosso por onde se toca a corda. */
function AlvoDaCorda({ linha, aoTocar }: { linha: readonly VetorTabletop[]; aoTocar: (e: MouseEvent, tipo: 'inicio' | 'clique') => void }) {
  const geometria = useMemo(() => geometriaDoTubo(linha, RAIO_ALVO_TOQUE_M), [linha])
  if (!geometria) return null
  return (
    <mesh
      name="corda-alvo"
      geometry={geometria}
      onPointerDown={(evento) => {
        evento.stopPropagation()
        aoTocar(evento.nativeEvent as unknown as MouseEvent, 'inicio')
      }}
      onClick={(evento) => {
        evento.stopPropagation()
        aoTocar(evento.nativeEvent as unknown as MouseEvent, 'clique')
      }}
    >
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  )
}

/** A corda em andamento: pontos já clicados, na cor da função escolhida, um pouco transparente. */
function DesenhoEmAndamento({
  cena,
  funcao,
  percurso,
}: {
  cena: CenaTabletop
  funcao: FuncaoCorda
  percurso: readonly ReferenciaPonto[]
}) {
  const cor = definicaoDaFuncao(funcao).cor
  const pontos = percurso.map((r) => localizarPontoNoMundo(cena, r))

  return (
    <group name="corda-em-desenho">
      {pontos.map((p, i) => {
        const anterior = pontos[i - 1]
        return p ? (
          <group key={i}>
            <mesh raycast={() => null} position={[p[0], p[1], p[2]]} renderOrder={11}>
              <sphereGeometry args={[0.06, 14, 10]} />
              <meshBasicMaterial color="#ffffff" depthTest={false} transparent opacity={0.9} />
            </mesh>
            {anterior && <SegmentoCorda de={anterior} para={p} cor={cor} raio={RAIO_CORDA_M * 1.2} opacidade={0.8} />}
          </group>
        ) : null
      })}
    </group>
  )
}


export function Cordas3D({
  objetos,
  cordas,
  ancoragens,
  idSelecionada,
  aoSelecionar,
  desenho,
  mostrarSetas = true,
}: {
  objetos: readonly ObjetoTabletop[]
  cordas: readonly CordaTabletop[]
  /** As ancoragens da cena: também giram os mosquetões em que as cordas se prendem. */
  ancoragens?: readonly AncoragemTabletop[]
  idSelecionada: string | null
  /** Sem isto as cordas só se veem; não se tocam. */
  aoSelecionar?: (id: string) => void
  desenho?: { funcao: FuncaoCorda; percurso: readonly ReferenciaPonto[] } | null
  /** Setas de tração: ao longo da corda puxada e nas pontas das demais (padrão ligado). */
  mostrarSetas?: boolean
}) {
  const cena = useMemo<CenaTabletop>(() => ({ versao: 2, nome: '', objetos, cordas, ancoragens }), [objetos, cordas, ancoragens])
  const desenhando = Boolean(desenho)
  return (
    <group name="cordas">
      {cordas.map((corda) => (
        <CordaNaCena
          key={corda.id}
          corda={corda}
          cena={cena}
          pontos={percursoNoMundo(cena, corda)}
          selecionada={corda.id === idSelecionada}
          // enquanto se desenha, a corda não rouba o toque dos pontos de ancoragem
          interativa={Boolean(aoSelecionar) && !desenhando}
          mostrarSetas={mostrarSetas}
          aoSelecionar={aoSelecionar ?? (() => {})}
        />
      ))}
      {desenho && <DesenhoEmAndamento cena={cena} funcao={desenho.funcao} percurso={desenho.percurso} />}
    </group>
  )
}
